// @flow

import React from 'react';
import { COUNTRIES } from '../const';
import type { ShoppingCenter } from '../types/shoppingCenterType';
import { store } from '../index';
import type { OfficePartial } from '../types/OfficesType';

export type UrlParam = {
  mall?: string,
  area?: string,
  token?: string,
  office?: string,
  buildingId?: string,
  store?: string,
  bp?: string,
  event?: string,
  opening?: string,
}

export function parseUrl(url: string): UrlParam {
  const queryParamString = url.split('?')[1];
  return parseQueryParams(queryParamString);
}

export const parseQueryParams = (queryParamString: string): UrlParam => {
  const searchParams = new URLSearchParams(queryParamString);
  const queryParams: UrlParam = {};
  searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });
  return queryParams;
};

export const buildQueryString = (urlParam: UrlParam): string => {
  const queryParamList = Object.entries(urlParam)
    .filter(([_, value]) => !!value)
    .map(([key, value]) => `${key}=${value}`);
  return (queryParamList.length > 0)
    ? `?${queryParamList.join('&')}`
    : '';
};

export const setQueryParams = (key: string, value: string) => {
  if ('URLSearchParams' in window) {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set(key, value);
    const query = !key || !value ? '' : `?${searchParams.toString()}`;
    const newRelativePathQuery = window.location.pathname + query;
    history.pushState(null, '', newRelativePathQuery);
  }
};

export const turanBaseURL = (): ?string => {
  const state = store.getState();
  if (!state.page.apolloConfiguration) {
    return null;
  }
  return state.page.apolloConfiguration.turanAdminUrl;
};

export const getCurrentPcId = (): string => {
  const params = parseUrl(window.location.href);
  return params.mall ? params.mall : '';
};

export const getCurrentOfficePcId = (): string => {
  const params = parseUrl(window.location.href);
  return params.office ? params.office : '';
};

export const getCurrentStore = (): string => {
  const params = parseUrl(window.location.href);
  return params.store ? params.store : '';
};

export const getCurrentCountry = (path: string) => {
  const params = parseUrl(path);
  return Object.keys(COUNTRIES).map(key => COUNTRIES[key]).filter(elt => elt.id === params.area)[0];
};

export const getCurrentShoppingCenter =
  (path: string, shoppingCenterList: ShoppingCenter[]): ShoppingCenter => {
    const params = parseUrl(path);
    return shoppingCenterList.filter(elt => elt.id === Number(params.mall))[0];
  };

export const getCurrentOffice =
  (path: string, officeList: OfficePartial[]): OfficePartial => {
    const params = parseUrl(path);
    return officeList.filter(elt => elt.id === params.office)[0];
  };

export const getCurrentShoppingCenterFromStore = (): ShoppingCenter => {
  const state = store.getState();
  const params = parseUrl(window.location.href);
  if (state.shoppingCenterFilters && state.shoppingCenterFilters.baseShoppingCenterList) {
    return state.shoppingCenterFilters.baseShoppingCenterList
      .filter(sc => String(sc.id) === String(params.mall))[0];
  }
  return null;
};

export const getShoppingCenter = (pcCode: string): ShoppingCenter => {
  const state = store.getState();
  if (state.shoppingCenterFilters && state.shoppingCenterFilters.baseShoppingCenterList) {
    return state.shoppingCenterFilters.baseShoppingCenterList.filter(sc => sc.pcCode === pcCode)[0];
  }
  return null;
};

export const getShoppingCenterNameByPcCode = (pcCode: string) => {
  const sc = getShoppingCenter(pcCode);
  return sc ? sc.name : pcCode;
};

export const getShoppingCenterNameByPcId = (pcId: string) => {
  const state = store.getState();
  if (state.shoppingCenterFilters && state.shoppingCenterFilters.baseShoppingCenterList) {
    const scId = parseInt(pcId, 10);
    return state.shoppingCenterFilters.baseShoppingCenterList.filter(sc => sc.id === scId)[0];
  }
  return null;
};
